import dayjsTZ, { dayjsInTz } from "../../../utils/dayjs";
import { clockingDayDisplayKey } from "../../../utils/timezone/dayBounds";
import { resolveClockingTimezone } from "../../../utils/timezone/teamTimezone";

/**
 * Memoized dashboard clocking selectors.
 * Cache keyed by clockings identity + company calendar day + programFields ref.
 */

let cache = {
  clockingsRef: null,
  todayKey: null,
  programFieldsRef: null,
  todaysClockings: [],
  openShifts: [],
  openShiftsToday: [],
  employeeIdToTodayClockings: new Map(),
  todayEmployeeIds: new Set(),
};

/** Company-default calendar day label (dashboard chrome). */
export function getTodayKey(now = dayjsTZ()) {
  return now.format("DD/MM/YYYY");
}

function rebuild(clockings, todayKey, programFields) {
  const list = Array.isArray(clockings) ? clockings : [];
  const todaysClockings = [];
  const openShifts = [];
  const openShiftsToday = [];
  const employeeIdToTodayClockings = new Map();

  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    const isOpen =
      item?.clockOutDate === null || item?.clockOutDate === undefined;
    if (isOpen) {
      openShifts.push(item);
    }

    if (!item?.clockInDate) continue;

    const tz = resolveClockingTimezone(item, item?.employeeTeam, programFields);
    const itemDay = clockingDayDisplayKey(item, programFields);
    const localToday = dayjsInTz(tz).format("DD/MM/YYYY");
    if (!itemDay || itemDay !== localToday) continue;

    todaysClockings.push(item);
    if (isOpen) {
      openShiftsToday.push(item);
    }
    const empId = item.employeeId;
    if (empId) {
      const bucket = employeeIdToTodayClockings.get(empId);
      if (bucket) {
        bucket.push(item);
      } else {
        employeeIdToTodayClockings.set(empId, [item]);
      }
    }
  }

  cache = {
    clockingsRef: clockings,
    todayKey,
    programFieldsRef: programFields,
    todaysClockings,
    openShifts,
    openShiftsToday,
    employeeIdToTodayClockings,
    todayEmployeeIds: new Set(employeeIdToTodayClockings.keys()),
  };
  return cache;
}

function ensureCache(clockings, programFields, todayKey = getTodayKey()) {
  if (
    cache.clockingsRef === clockings &&
    cache.todayKey === todayKey &&
    cache.programFieldsRef === programFields
  ) {
    return cache;
  }
  return rebuild(clockings, todayKey, programFields);
}

function programFieldsFrom(state) {
  return state?.programFields?.programFields;
}

/** @param {{ clockings?: { clockings?: Array }, programFields?: { programFields?: Array } }} state */
export function selectTodaysClockings(state) {
  const clockings = state?.clockings?.clockings;
  return ensureCache(clockings, programFieldsFrom(state)).todaysClockings;
}

/** Open shifts in the loaded window (clockOutDate null). */
export function selectOpenShifts(state) {
  const clockings = state?.clockings?.clockings;
  return ensureCache(clockings, programFieldsFrom(state)).openShifts;
}

/** Open shifts that started today (per clocking TZ). */
export function selectOpenShiftsToday(state) {
  const clockings = state?.clockings?.clockings;
  return ensureCache(clockings, programFieldsFrom(state)).openShiftsToday;
}

/** Map employeeId -> today's clocking rows. */
export function selectEmployeeIdToTodayClockings(state) {
  const clockings = state?.clockings?.clockings;
  return ensureCache(clockings, programFieldsFrom(state))
    .employeeIdToTodayClockings;
}

/** Set of employeeIds who clocked in today. */
export function selectTodayEmployeeIds(state) {
  const clockings = state?.clockings?.clockings;
  return ensureCache(clockings, programFieldsFrom(state)).todayEmployeeIds;
}

/** Test helper — clear module cache. */
export function __resetTodayClockingsCache() {
  cache = {
    clockingsRef: null,
    todayKey: null,
    programFieldsRef: null,
    todaysClockings: [],
    openShifts: [],
    openShiftsToday: [],
    employeeIdToTodayClockings: new Map(),
    todayEmployeeIds: new Set(),
  };
}
